import React, { useEffect, useMemo, useState } from 'react';
import { fromIso } from '../../utils/businessDate';
import { getTenantToday } from '../../utils/dateUtils';
import axios from 'axios';
import styled from 'styled-components';
import {
  PortcoGlobal,
  Surface,
  Header,
  HeaderInner,
  HeaderBrand,
  HeaderBrandMark,
  HeaderCrumb,
  Body,
  Hero,
  HeroEyebrow,
  HeroTitle,
  HeroSub,
  StatStrip,
  StatCell,
  StatLabel,
  StatValue,
  StatSub,
  Panel,
  PanelHead,
  PanelTitle,
  PanelMeta,
  PanelBody,
  Table,
  Thead,
  Th,
  Td,
  TdMono,
  Tr,
  Loading,
  Empty,
  portcoTokens,
} from '../portco/PortcoTheme';

import { DateField } from '../shared/DateField';

/**
 * Alts Regulatory Reporting (Phase B3) — generation surface for the four
 * alternatives-specific regulatory templates that AltsRegulatoryReportingService
 * builds. Companion to /regulatory which covers FI/equity reporting (Form 13F,
 * NAIC, etc.).
 *
 * Generators:
 *   POST /api/regulatory/alts/aifmd/funds/{fundId}                  — XML
 *   POST /api/regulatory/alts/sfdr-pai/funds/{fundId}               — JSON
 *   POST /api/regulatory/alts/bdc/funds/{fundId}                    — JSON
 *   POST /api/regulatory/alts/form-pf-section-4/groups/{groupId}    — XML
 *
 * Audit:
 *   GET /api/regulatory/alts/filings
 *   GET /api/regulatory/alts/filings/{filingId}
 */

interface FilingRow {
  id: number;
  filing_type: string;
  period_end_date?: string;
  fund_id?: number;
  fund_name?: string;
  adviser_name?: string;
  payload_format?: string;
  schema_version?: string;
  status?: string;
  submitted_at?: string;
  accepted_at?: string;
  regulator_reference?: string;
  created_at?: string;
  created_by?: string;
}

interface FundLite {
  id: number;
  fundName?: string;
  manager?: string;
  vintageYear?: number;
}

interface PortfolioGroupLite {
  id: number;
  groupName?: string;
}

type FilingType = 'AIFMD_ANNEX_IV' | 'SFDR_PAI' | 'BDC_QUARTERLY' | 'FORM_PF_SECTION_4';

const FILING_TYPES: { key: FilingType; label: string; rationale: string; tone: string; format: string }[] = [
  { key: 'AIFMD_ANNEX_IV', label: 'AIFMD Annex IV', rationale: 'EU AIFMD Annex IV: leverage (gross + commitment), 5 main exposures, principal markets — XML.', tone: '#6366f1', format: 'XML · FCA / CSSF / AFM' },
  { key: 'SFDR_PAI', label: 'SFDR PAI', rationale: '18 mandatory PAI indicators + Article 8/9 product disclosure (NOT_AVAILABLE markers when ESG feed absent).', tone: '#14b8a6', format: 'JSON · Article 8 / 9' },
  { key: 'BDC_QUARTERLY', label: 'BDC Quarterly', rationale: 'BDC 10-Q/10-K supplemental: portfolio company table, NAV-per-share walk, fee waiver detail.', tone: '#0ea5e9', format: 'JSON · SEC supplemental' },
  { key: 'FORM_PF_SECTION_4', label: 'Form PF §4', rationale: 'Large hedge fund / large PE adviser detailed sections — counterparty + geographic exposures.', tone: '#a855f7', format: 'XML · SEC' },
];

const TYPE_BG: Record<string, string> = {
  AIFMD_ANNEX_IV: '#6366f1',
  SFDR_PAI: '#14b8a6',
  BDC_QUARTERLY: '#0ea5e9',
  FORM_PF_SECTION_4: '#a855f7',
};

const FilterChips = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;
const Chip = styled.button<{ $active?: boolean }>`
  font-family: ${portcoTokens.fontSans};
  font-size: 11px;
  letter-spacing: 0.04em;
  padding: 4px 10px;
  border-radius: 999px;
  cursor: pointer;
  background: ${(p) => (p.$active ? portcoTokens.accent : portcoTokens.surfaceTile)};
  color: ${(p) => (p.$active ? '#fff' : portcoTokens.inkBody)};
  border: 1px solid ${(p) => (p.$active ? portcoTokens.accent : portcoTokens.hairline)};
  text-transform: uppercase;
  &:hover {
    border-color: ${portcoTokens.accent};
  }
`;
const TypeBadge = styled.span<{ $type: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  letter-spacing: 0.05em;
  font-weight: 600;
  text-transform: uppercase;
  background: ${(p) => `${TYPE_BG[p.$type] || '#94a3b8'}22`};
  color: ${(p) => TYPE_BG[p.$type] || '#94a3b8'};
  border: 1px solid ${(p) => `${TYPE_BG[p.$type] || '#94a3b8'}66`};
`;
const GenGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 14px;
`;
const GenCard = styled.div<{ $tone: string }>`
  border: 1px solid ${portcoTokens.hairline};
  border-left: 4px solid ${(p) => p.$tone};
  border-radius: 6px;
  padding: 14px 16px;
  background: ${portcoTokens.surfaceTile};
`;
const GenLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${portcoTokens.inkPrimary};
  margin-bottom: 4px;
`;
const GenFormat = styled.div`
  font-size: 10px;
  color: ${portcoTokens.inkSubtle};
  letter-spacing: 0.04em;
  margin-bottom: 8px;
`;
const GenRationale = styled.div`
  font-size: 12px;
  color: ${portcoTokens.inkBody};
  line-height: 1.5;
  margin-bottom: 12px;
`;
const FieldRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;
const FieldGroup = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 10px;
  letter-spacing: 0.04em;
  color: ${portcoTokens.inkSubtle};
  text-transform: uppercase;
  flex: 1;
  min-width: 110px;
`;
const Select = styled.select`
  font-family: ${portcoTokens.fontSans};
  font-size: 12px;
  padding: 5px 8px;
  border: 1px solid ${portcoTokens.hairline};
  border-radius: 4px;
  background: #fff;
  color: ${portcoTokens.inkPrimary};
`;
const GenBtn = styled.button<{ $tone: string }>`
  font-family: ${portcoTokens.fontSans};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 7px 14px;
  border: 1px solid ${(p) => p.$tone};
  background: ${(p) => p.$tone};
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;
const ErrorBanner = styled.div`
  padding: 10px 14px;
  border: 1px solid ${portcoTokens.statusBreachedSubtle};
  background: ${portcoTokens.statusBreachedSubtle};
  color: ${portcoTokens.statusBreached};
  font-size: 12px;
  border-radius: 4px;
  margin-bottom: 12px;
`;
const SuccessBanner = styled.div`
  padding: 10px 14px;
  border: 1px solid ${portcoTokens.statusPassingSubtle};
  background: ${portcoTokens.statusPassingSubtle};
  color: ${portcoTokens.statusPassing};
  font-size: 12px;
  border-radius: 4px;
  margin-bottom: 12px;
`;

// ---------- helpers ----------
function defaultPeriodEnd(): string {
  const now = fromIso(getTenantToday());
  const m = now.getMonth();
  const y = now.getFullYear();
  const lastQ = Math.floor(m / 3) - 1;
  if (lastQ < 0) return `${y - 1}-12-31`;
  const month = (lastQ + 1) * 3;
  const day = month === 6 ? 30 : month === 9 ? 30 : 31;
  return `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
const fmtDate = (s?: string) => (s ? s.slice(0, 10) : '—');

const AltsRegulatoryReportingScreen: React.FC = () => {
  const [filings, setFilings] = useState<FilingRow[] | null>(null);
  const [filingsLoading, setFilingsLoading] = useState(true);
  const [funds, setFunds] = useState<FundLite[]>([]);
  const [groups, setGroups] = useState<PortfolioGroupLite[]>([]);
  const [filterType, setFilterType] = useState<FilingType | 'ALL'>('ALL');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // per-card form state
  const [aifmdFundId, setAifmdFundId] = useState<string>('');
  const [aifmdPeriod, setAifmdPeriod] = useState<string>(defaultPeriodEnd());
  const [sfdrFundId, setSfdrFundId] = useState<string>('');
  const [sfdrPeriod, setSfdrPeriod] = useState<string>(defaultPeriodEnd());
  const [sfdrArticle, setSfdrArticle] = useState<string>('ARTICLE_8');
  const [bdcFundId, setBdcFundId] = useState<string>('');
  const [bdcPeriod, setBdcPeriod] = useState<string>(defaultPeriodEnd());
  const [pfGroupId, setPfGroupId] = useState<string>('');
  const [pfPeriod, setPfPeriod] = useState<string>(defaultPeriodEnd());
  const [pfFilingType, setPfFilingType] = useState<string>('LARGE_PE_ADVISER');
  const [submitting, setSubmitting] = useState<FilingType | null>(null);

  const refreshFilings = async () => {
    setFilingsLoading(true);
    try {
      const r = await axios.get<FilingRow[]>('/api/regulatory/alts/filings', { params: { limit: 100 } });
      setFilings(r.data ?? []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load filings');
      setFilings([]);
    } finally {
      setFilingsLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [fnds, grps] = await Promise.all([
          axios.get<any>('/api/pe/funds').catch(() => ({ data: [] })),
          axios.get<any>('/api/dimensions/portfolio-groups').catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const fundList: FundLite[] = Array.isArray(fnds.data)
          ? fnds.data
          : fnds.data?.content ?? [];
        const groupList: PortfolioGroupLite[] = Array.isArray(grps.data)
          ? grps.data
          : grps.data?.content ?? [];
        setFunds(fundList);
        setGroups(groupList);
        await refreshFilings();
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load reference data');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = async (kind: FilingType) => {
    setSubmitting(kind);
    setError(null);
    setInfo(null);
    try {
      let resp: any;
      if (kind === 'AIFMD_ANNEX_IV') {
        if (!aifmdFundId) throw new Error('Pick a fund.');
        resp = await axios.post(`/api/regulatory/alts/aifmd/funds/${aifmdFundId}?periodEnd=${aifmdPeriod}`);
      } else if (kind === 'SFDR_PAI') {
        if (!sfdrFundId) throw new Error('Pick a fund.');
        resp = await axios.post(`/api/regulatory/alts/sfdr-pai/funds/${sfdrFundId}?periodEnd=${sfdrPeriod}&article=${sfdrArticle}`);
      } else if (kind === 'BDC_QUARTERLY') {
        if (!bdcFundId) throw new Error('Pick a fund.');
        resp = await axios.post(`/api/regulatory/alts/bdc/funds/${bdcFundId}?periodEnd=${bdcPeriod}`);
      } else if (kind === 'FORM_PF_SECTION_4') {
        if (!pfGroupId) throw new Error('Pick a portfolio group.');
        resp = await axios.post(`/api/regulatory/alts/form-pf-section-4/groups/${pfGroupId}?periodEnd=${pfPeriod}&filingType=${pfFilingType}`);
      }
      const filingId = resp?.data?.filingId;
      setInfo(`Filing #${filingId} generated (${kind}).`);
      await refreshFilings();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || `Generation failed: ${kind}`);
    } finally {
      setSubmitting(null);
    }
  };

  const filteredFilings = useMemo(() => {
    if (!filings) return [];
    if (filterType === 'ALL') return filings;
    return filings.filter((f) => f.filing_type === filterType);
  }, [filings, filterType]);

  const summary = useMemo(() => {
    const out: Record<string, number> = { AIFMD_ANNEX_IV: 0, SFDR_PAI: 0, BDC_QUARTERLY: 0, FORM_PF_SECTION_4: 0 };
    (filings ?? []).forEach((f) => {
      if (out[f.filing_type] !== undefined) out[f.filing_type] += 1;
    });
    return out;
  }, [filings]);

  return (
    <>
      <PortcoGlobal />
      <Surface className="portco-surface">
        <Header>
          <HeaderInner>
            <HeaderBrand>
              <HeaderBrandMark>AF</HeaderBrandMark>
              <span>AxiomFlow / Regulatory / Alternatives</span>
            </HeaderBrand>
            <HeaderCrumb>
              <span>AIFMD · SFDR · BDC · Form PF §4</span>
            </HeaderCrumb>
          </HeaderInner>
        </Header>

        <Body>
          <Hero>
            <HeroEyebrow>Alts regulatory templates</HeroEyebrow>
            <HeroTitle>Alternative-asset filings — generation + audit</HeroTitle>
            <HeroSub>
              Phase B3 templates: AIFMD Annex IV (XML), SFDR PAI (JSON, 18 indicators),
              BDC supplemental (JSON), and Form PF Section 4 (XML). Each filing persists to{' '}
              <code>alts_regulatory_filing</code> with bi-temporal audit + payload retained.
            </HeroSub>
          </Hero>

          {error && <ErrorBanner>{error}</ErrorBanner>}
          {info && <SuccessBanner>{info}</SuccessBanner>}

          <StatStrip>
            <StatCell>
              <StatLabel>AIFMD Annex IV</StatLabel>
              <StatValue>{summary.AIFMD_ANNEX_IV}</StatValue>
              <StatSub>filings to date</StatSub>
            </StatCell>
            <StatCell>
              <StatLabel>SFDR PAI</StatLabel>
              <StatValue>{summary.SFDR_PAI}</StatValue>
              <StatSub>Article 8 / 9</StatSub>
            </StatCell>
            <StatCell>
              <StatLabel>BDC Quarterly</StatLabel>
              <StatValue>{summary.BDC_QUARTERLY}</StatValue>
              <StatSub>SEC supplemental</StatSub>
            </StatCell>
            <StatCell>
              <StatLabel>Form PF §4</StatLabel>
              <StatValue>{summary.FORM_PF_SECTION_4}</StatValue>
              <StatSub>SEC large-adviser</StatSub>
            </StatCell>
          </StatStrip>

          <Panel>
            <PanelHead>
              <PanelTitle>Generators</PanelTitle>
              <PanelMeta>Pick a fund or portfolio group + period; payload is built from IBOR data.</PanelMeta>
            </PanelHead>
            <PanelBody>
              <GenGrid>
                {/* AIFMD */}
                <GenCard $tone={FILING_TYPES[0].tone}>
                  <GenLabel>AIFMD Annex IV</GenLabel>
                  <GenFormat>{FILING_TYPES[0].format}</GenFormat>
                  <GenRationale>{FILING_TYPES[0].rationale}</GenRationale>
                  <FieldRow>
                    <FieldGroup>
                      Fund
                      <Select value={aifmdFundId} onChange={(e) => setAifmdFundId(e.target.value)}>
                        <option value="">— select —</option>
                        {funds.map((f) => (
                          <option key={f.id} value={f.id}>{f.fundName ?? `Fund ${f.id}`}</option>
                        ))}
                      </Select>
                    </FieldGroup>
                    <FieldGroup>
                      Period end
                      <DateField tone="light" value={aifmdPeriod ?? ''} onChange={iso => setAifmdPeriod(iso)} />
                    </FieldGroup>
                  </FieldRow>
                  <GenBtn
                    $tone={FILING_TYPES[0].tone}
                    onClick={() => submit('AIFMD_ANNEX_IV')}
                    disabled={submitting === 'AIFMD_ANNEX_IV'}
                  >
                    {submitting === 'AIFMD_ANNEX_IV' ? 'Generating…' : 'Generate AIFMD'}
                  </GenBtn>
                </GenCard>

                {/* SFDR */}
                <GenCard $tone={FILING_TYPES[1].tone}>
                  <GenLabel>SFDR PAI</GenLabel>
                  <GenFormat>{FILING_TYPES[1].format}</GenFormat>
                  <GenRationale>{FILING_TYPES[1].rationale}</GenRationale>
                  <FieldRow>
                    <FieldGroup>
                      Fund
                      <Select value={sfdrFundId} onChange={(e) => setSfdrFundId(e.target.value)}>
                        <option value="">— select —</option>
                        {funds.map((f) => (
                          <option key={f.id} value={f.id}>{f.fundName ?? `Fund ${f.id}`}</option>
                        ))}
                      </Select>
                    </FieldGroup>
                    <FieldGroup>
                      Article
                      <Select value={sfdrArticle} onChange={(e) => setSfdrArticle(e.target.value)}>
                        <option value="ARTICLE_8">Article 8</option>
                        <option value="ARTICLE_9">Article 9</option>
                      </Select>
                    </FieldGroup>
                    <FieldGroup>
                      Period end
                      <DateField tone="light" value={sfdrPeriod ?? ''} onChange={iso => setSfdrPeriod(iso)} />
                    </FieldGroup>
                  </FieldRow>
                  <GenBtn
                    $tone={FILING_TYPES[1].tone}
                    onClick={() => submit('SFDR_PAI')}
                    disabled={submitting === 'SFDR_PAI'}
                  >
                    {submitting === 'SFDR_PAI' ? 'Generating…' : 'Generate SFDR'}
                  </GenBtn>
                </GenCard>

                {/* BDC */}
                <GenCard $tone={FILING_TYPES[2].tone}>
                  <GenLabel>BDC Quarterly</GenLabel>
                  <GenFormat>{FILING_TYPES[2].format}</GenFormat>
                  <GenRationale>{FILING_TYPES[2].rationale}</GenRationale>
                  <FieldRow>
                    <FieldGroup>
                      Fund
                      <Select value={bdcFundId} onChange={(e) => setBdcFundId(e.target.value)}>
                        <option value="">— select —</option>
                        {funds.map((f) => (
                          <option key={f.id} value={f.id}>{f.fundName ?? `Fund ${f.id}`}</option>
                        ))}
                      </Select>
                    </FieldGroup>
                    <FieldGroup>
                      Period end
                      <DateField tone="light" value={bdcPeriod ?? ''} onChange={iso => setBdcPeriod(iso)} />
                    </FieldGroup>
                  </FieldRow>
                  <GenBtn
                    $tone={FILING_TYPES[2].tone}
                    onClick={() => submit('BDC_QUARTERLY')}
                    disabled={submitting === 'BDC_QUARTERLY'}
                  >
                    {submitting === 'BDC_QUARTERLY' ? 'Generating…' : 'Generate BDC'}
                  </GenBtn>
                </GenCard>

                {/* Form PF §4 */}
                <GenCard $tone={FILING_TYPES[3].tone}>
                  <GenLabel>Form PF §4</GenLabel>
                  <GenFormat>{FILING_TYPES[3].format}</GenFormat>
                  <GenRationale>{FILING_TYPES[3].rationale}</GenRationale>
                  <FieldRow>
                    <FieldGroup>
                      Portfolio group
                      <Select value={pfGroupId} onChange={(e) => setPfGroupId(e.target.value)}>
                        <option value="">— select —</option>
                        {groups.map((g) => (
                          <option key={g.id} value={g.id}>{g.groupName ?? `Group ${g.id}`}</option>
                        ))}
                      </Select>
                    </FieldGroup>
                    <FieldGroup>
                      Filing type
                      <Select value={pfFilingType} onChange={(e) => setPfFilingType(e.target.value)}>
                        <option value="LARGE_PE_ADVISER">Large PE adviser</option>
                        <option value="LARGE_HEDGE_FUND_ADVISER">Large hedge fund adviser</option>
                      </Select>
                    </FieldGroup>
                    <FieldGroup>
                      Period end
                      <DateField tone="light" value={pfPeriod ?? ''} onChange={iso => setPfPeriod(iso)} />
                    </FieldGroup>
                  </FieldRow>
                  <GenBtn
                    $tone={FILING_TYPES[3].tone}
                    onClick={() => submit('FORM_PF_SECTION_4')}
                    disabled={submitting === 'FORM_PF_SECTION_4'}
                  >
                    {submitting === 'FORM_PF_SECTION_4' ? 'Generating…' : 'Generate Form PF §4'}
                  </GenBtn>
                </GenCard>
              </GenGrid>
            </PanelBody>
          </Panel>

          <Panel>
            <PanelHead>
              <PanelTitle>Filings history</PanelTitle>
              <PanelMeta>Most recent 100, newest first.</PanelMeta>
            </PanelHead>
            <PanelBody>
              <FilterChips style={{ marginBottom: 12 }}>
                <Chip $active={filterType === 'ALL'} onClick={() => setFilterType('ALL')}>
                  All ({filings?.length ?? 0})
                </Chip>
                {FILING_TYPES.map((t) => (
                  <Chip key={t.key} $active={filterType === t.key} onClick={() => setFilterType(t.key)}>
                    {t.label} ({summary[t.key]})
                  </Chip>
                ))}
              </FilterChips>

              {filingsLoading && <Loading>Loading filings…</Loading>}
              {!filingsLoading && filteredFilings.length === 0 && (
                <Empty>No filings yet — use the generators above to create the first.</Empty>
              )}
              {!filingsLoading && filteredFilings.length > 0 && (
                <Table>
                  <Thead>
                    <Tr>
                      <Th>Type</Th>
                      <Th>Fund / Adviser</Th>
                      <Th>Period</Th>
                      <Th>Schema</Th>
                      <Th>Status</Th>
                      <Th>Created</Th>
                      <Th>By</Th>
                    </Tr>
                  </Thead>
                  <tbody>
                    {filteredFilings.map((f) => (
                      <Tr key={f.id}>
                        <Td><TypeBadge $type={f.filing_type}>{labelFor(f.filing_type)}</TypeBadge></Td>
                        <Td>{f.fund_name ?? f.adviser_name ?? (f.fund_id ? `Fund ${f.fund_id}` : '—')}</Td>
                        <TdMono>{fmtDate(f.period_end_date)}</TdMono>
                        <Td>
                          {f.schema_version ?? '—'}
                          {f.payload_format ? ` · ${f.payload_format}` : ''}
                        </Td>
                        <Td>{f.status ?? '—'}</Td>
                        <TdMono>{fmtDate(f.created_at)}</TdMono>
                        <Td>{f.created_by ?? '—'}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </PanelBody>
          </Panel>
        </Body>
      </Surface>
    </>
  );
};

function labelFor(t: string): string {
  const f = FILING_TYPES.find((x) => x.key === t);
  return f?.label ?? t;
}

export default AltsRegulatoryReportingScreen;
